// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Manages text extraction admin configuration state and syncs changes to parent.
 */
import { useEffect, useRef, useState } from 'react'

interface Params {
  config?: Record<string, unknown>
  onChange?: (key: string, value: unknown) => void
}

interface UseTextExtractionAdminConfig {
  detectFormatting: boolean
  setDetectFormatting: (value: boolean) => void
  forceExtraction: boolean
  setForceExtraction: (value: boolean) => void
  extractTrackedChanges: boolean
  setExtractTrackedChanges: (value: boolean) => void
  acceptTrackedChanges: boolean
  setAcceptTrackedChanges: (value: boolean) => void
  extractComments: boolean
  setExtractComments: (value: boolean) => void
}

export const useTextExtractionAdminConfig = ({ config, onChange }: Params): UseTextExtractionAdminConfig => {
  // Settings are now flat (no defaultConfig nesting)
  const settings = config as Record<string, unknown> | undefined

  // Stable ref for callback to avoid restarting effect when parent re-renders
  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  const [detectFormatting, setDetectFormatting] = useState<boolean>(
    settings?.detectFormatting === true
  )
  const [forceExtraction, setForceExtraction] = useState<boolean>(
    settings?.forceExtraction === true
  )
  const [extractTrackedChanges, setExtractTrackedChanges] = useState<boolean>(
    settings?.extractTrackedChanges === true
  )
  const [acceptTrackedChanges, setAcceptTrackedChanges] = useState<boolean>(
    settings?.acceptTrackedChanges === true
  )
  const [extractComments, setExtractComments] = useState<boolean>(
    settings?.extractComments === true
  )

  // Sync state changes to parent via onChange callback (flat structure)
  useEffect(() => {
    onChangeRef.current?.('detectFormatting', detectFormatting)
    onChangeRef.current?.('forceExtraction', forceExtraction)
    onChangeRef.current?.('extractTrackedChanges', extractTrackedChanges)
    onChangeRef.current?.('acceptTrackedChanges', acceptTrackedChanges)
    onChangeRef.current?.('extractComments', extractComments)
  }, [detectFormatting, forceExtraction, extractTrackedChanges, acceptTrackedChanges, extractComments])

  return {
    detectFormatting, setDetectFormatting,
    forceExtraction, setForceExtraction,
    extractTrackedChanges, setExtractTrackedChanges,
    acceptTrackedChanges, setAcceptTrackedChanges,
    extractComments, setExtractComments,
  }
}
